import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from "@angular/core";
import { Subject } from "rxjs";
import { TranslocoService } from "@jsverse/transloco";
import { DatePipe } from "@angular/common";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { delay, takeUntil } from "rxjs/operators";
import { RangePickerComponent } from "./range-picker.component";
import { RangePickerPeriod, RangePickerState } from "./range-picker-component.store";
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfDay,
} from "date-fns";
import { assign } from "lodash";
import dateutil from "rrule/dist/esm/src/dateutil";
import daysBetween = dateutil.daysBetween;

@Component({
    selector: "stats-date-range-picker",
    templateUrl: "./date-range-picker.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DatePipe],
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
    private _unsubscribeAll = new Subject();

    @ViewChild("periodPicker") private _periodPicker: TemplateRef<any>;

    @ViewChild("periodPickerOrigin") private _periodPickerOrigin: ElementRef;

    private _periodPickerOverlayRef: OverlayRef;

    @ViewChild("periodPickerHost", { read: ViewContainerRef }) periodPickerHost;

    _state: RangePickerState;

    text: string;

    @Input()
    set state(value: RangePickerState) {
        this._state = value;
        let params = {
            periodStart: this._datePipe.transform(value.periodStart, "dd/MM/yyyy"),
            periodEnd: this._datePipe.transform(value.periodEnd, "dd/MM/yyyy"),
            month: this._datePipe.transform(value.periodStart, "MMMM"),
            year: this._datePipe.transform(value.periodStart, "yyyy"),
            weekStart: this._datePipe.transform(value.periodStart, "dd/MM"),
            weekEnd: this._datePipe.transform(value.periodEnd, "dd/MM"),
        };
        this.text = this._translateService.translate(`date-range-picker.FORMAT_${value.period}`, params);
    }

    @Input()
    disabled: boolean = false;

    get state() {
        return this._state;
    }

    @Output()
    stateChanged = new EventEmitter<RangePickerState>();

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _datePipe: DatePipe,
        private _translateService: TranslocoService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _componentFactoryResolver: ComponentFactoryResolver
    ) {}

    ngOnInit(): void {}

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    openPicker(): void {
        this._periodPickerOverlayRef = this._overlay.create({
            backdropClass: "",
            hasBackdrop: true,
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._periodPickerOrigin.nativeElement)
                .withFlexibleDimensions(false)
                .withViewportMargin(64)
                .withLockedPosition(true)
                .withPositions([
                    {
                        originX: "start",
                        originY: "top",
                        overlayX: "start",
                        overlayY: "top",
                    },
                ]),
        });

        // Subscribe to the attachments observable
        this._periodPickerOverlayRef
            .attachments()
            .pipe(delay(100))
            .subscribe(() => {
                const factory = this._componentFactoryResolver.resolveComponentFactory(RangePickerComponent);
                const componentRef = this.periodPickerHost.createComponent(factory);
                const instance = componentRef.instance as RangePickerComponent;
                instance.initialState = { ...this.state };
                instance.onFilter.pipe(takeUntil(this._periodPickerOverlayRef.detachments())).subscribe((state) => {
                    this.state = state;
                    this.stateChanged.emit(this.state);
                    this._periodPickerOverlayRef.detach();
                    this._changeDetector.detectChanges();
                });
                instance.onClose.pipe(takeUntil(this._periodPickerOverlayRef.detachments())).subscribe((state) => {
                    this._periodPickerOverlayRef.detach();
                });
                this._changeDetector.detectChanges();
            });

        const templatePortal = new TemplatePortal(this._periodPicker, this._viewContainerRef);

        this._periodPickerOverlayRef.attach(templatePortal);

        this._periodPickerOverlayRef.backdropClick().subscribe(() => {
            if (this._periodPickerOverlayRef && this._periodPickerOverlayRef.hasAttached()) {
                this._periodPickerOverlayRef.detach();
            }

            if (templatePortal && templatePortal.isAttached) {
                templatePortal.detach();
            }
        });
    }

    next() {
        let start = this.state.periodStart;
        let end = this.state.periodEnd;
        switch (this.state.period) {
            case RangePickerPeriod.daily:
                start = addDays(start, 1);
                end = endOfDay(start);
                break;
            case RangePickerPeriod.weekly:
                start = addWeeks(start, 1);
                end = endOfWeek(start);
                break;
            case RangePickerPeriod.monthly:
                start = addMonths(start, 1);
                end = endOfMonth(start);
                break;
            case RangePickerPeriod.yearly:
                start = addYears(start, 1);
                end = endOfYear(start);
                break;
            case RangePickerPeriod.custom:
                let daysDiff = daysBetween(end, start) + 1;
                start = startOfDay(addDays(start, daysDiff));
                end = endOfDay(addDays(end, daysDiff));
                break;
        }
        this.state = assign(new RangePickerState(), {
            period: this.state.period,
            periodStart: startOfDay(start),
            periodEnd: endOfDay(end),
        });
        this.stateChanged.emit(this.state);
        this._changeDetector.detectChanges();
    }

    prev() {
        let start = this.state.periodStart;
        let end = this.state.periodEnd;
        switch (this.state.period) {
            case RangePickerPeriod.daily:
                start = addDays(start, -1);
                end = endOfDay(start);
                break;
            case RangePickerPeriod.weekly:
                start = addWeeks(start, -1);
                end = endOfWeek(start);
                break;
            case RangePickerPeriod.monthly:
                start = addMonths(start, -1);
                end = endOfMonth(start);
                break;
            case RangePickerPeriod.yearly:
                start = addYears(start, -1);
                end = endOfYear(start);
                break;
        }
        this.state = assign(new RangePickerState(), {
            period: this.state.period,
            periodStart: startOfDay(start),
            periodEnd: endOfDay(end),
        });
        this.stateChanged.emit(this.state);
        this._changeDetector.detectChanges();
    }
}
